import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { ApiError, api, type CatalogPreferences, type LibrarySort } from '../api.ts';
import {
  DEFAULT_CATALOG_PREFERENCES,
  isDefaultCatalogPreferences,
  LIBRARY_SORT_OPTIONS,
  normalizeCatalogPreferences,
  WATCHLIST_LAYOUT_OPTIONS,
} from '../catalogPrefs.ts';
import { CheckIcon, FolderIcon, RefreshIcon } from '../Icon.tsx';

/**
 * Katalog tercihleri: hesabı izleyen varsayılan kütüphane sıralaması ve Listem
 * görünümü. Kütüphane içinde yapılan anlık seçim oturumda kalır; bu kart
 * yalnızca yeni açılışlarda kullanılan varsayılanı belirler.
 */
export function CatalogPreferencesCard(): ReactNode {
  const [value, setValue] = useState<CatalogPreferences>(DEFAULT_CATALOG_PREFERENCES);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .catalogPreferences()
      .then((result) => {
        if (!cancelled) setValue(normalizeCatalogPreferences(result));
      })
      .catch(() => {
        if (!cancelled) {
          setValue(DEFAULT_CATALOG_PREFERENCES);
          setError('Katalog tercihleri okunamadı; varsayılanlar gösteriliyor.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const persist = async (next: CatalogPreferences): Promise<void> => {
    const previous = value;
    setValue(next);
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setValue(normalizeCatalogPreferences(await api.updateCatalogPreferences(next)));
      setNotice('Katalog tercihleri kaydedildi.');
    } catch (caught) {
      setValue(previous);
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Katalog tercihleri kaydedilemedi. Bağlantıyı denetleyin.',
      );
    } finally {
      setBusy(false);
    }
  };

  const reset = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setValue(normalizeCatalogPreferences(await api.resetCatalogPreferences()));
      setNotice('Katalog varsayılanlarına dönüldü.');
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Varsayılana dönülemedi. Bağlantıyı denetleyin.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="account__card account__card--compact" aria-busy={busy}>
      <div className="account__card-header">
        <div className="account__card-icon">
          <FolderIcon size={20} />
        </div>
        <div>
          <h2 className="account__card-title">Katalog</h2>
          <p className="account__card-desc">
            Kütüphane ve Listem ekranlarının başlangıç düzeni; diğer cihazlarda da geçerli.
          </p>
        </div>
      </div>

      {!loaded ? (
        <p className="account__hint" role="status">
          Katalog tercihleri yükleniyor…
        </p>
      ) : (
        <div className="catalog-prefs">
          <label className="catalog-prefs__row">
            <span>
              <strong>Varsayılan kütüphane sıralaması</strong>
              <small>Kütüphane açıldığında kullanılacak sıralama.</small>
            </span>
            <select
              className="catalog-prefs__select"
              value={value.defaultLibrarySort}
              disabled={busy}
              onChange={(event) =>
                void persist({
                  ...value,
                  defaultLibrarySort: event.target.value as LibrarySort,
                })
              }
            >
              {LIBRARY_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="catalog-prefs__row">
            <span>
              <strong>Listem görünümü</strong>
              <small>Listem ekranını ızgara veya satır listesi olarak aç.</small>
            </span>
            <select
              className="catalog-prefs__select"
              value={value.watchlistLayout}
              disabled={busy}
              onChange={(event) =>
                void persist({
                  ...value,
                  watchlistLayout: event.target.value as CatalogPreferences['watchlistLayout'],
                })
              }
            >
              {WATCHLIST_LAYOUT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {error !== null && (
        <p className="home-prefs__error" role="alert">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="home-prefs__notice" role="status">
          <CheckIcon size={13} /> {notice}
        </p>
      )}

      <div className="home-prefs__footer">
        <span className="account__hint">Kütüphane içindeki anlık seçimler bu oturumda kalır.</span>
        <button
          type="button"
          className="button button--ghost home-prefs__reset"
          disabled={busy || isDefaultCatalogPreferences(value)}
          onClick={() => void reset()}
        >
          <RefreshIcon size={14} />
          Varsayılana dön
        </button>
      </div>
    </section>
  );
}
